/**
 * @fileoverview Input/output schemas for structural-refresh-composite-v1.
 *
 * This composite agent orchestrates a two-step pipeline:
 *   1. divergence-report-v2 → diagnose structural drift
 *   2. structural-suggestions-v1 → propose concrete changes
 *
 * Input combines what both agents need (divergence report inputs + existing storylines).
 * Output includes both the diagnostic report and the actionable suggestions.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  OutputSchema as DivergenceReportOutputSchema,
} from '@/ai/agents/primitives/story-updating/divergence-report-v2/schema';
import {
  OutputSchema as StructuralSuggestionsOutputSchema,
} from '@/ai/agents/primitives/story-updating/structural-suggestions-v1/schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Union of inputs needed by both child agents.
 *
 * divergence-report-v2 needs: plannedPlot, actualPlotProgression,
 *   storylineDivergenceData, characterDivergenceData, plotThreadStatus
 *
 * structural-suggestions-v1 needs: existingStorylines, chapterSummaries,
 *   storyContext, divergenceReport (produced by step 1)
 */
export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  // For divergence-report-v2
  ...AGENT_SCHEMA_OBJECTS.plannedPlot.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.actualPlotProgression.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storylineDivergenceData.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characterDivergenceData.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.plotThreadStatus.schema.shape,
  // For structural-suggestions-v1
  ...AGENT_SCHEMA_OBJECTS.existingStorylines.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterSummaries.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  /** Diagnostic output from divergence-report-v2. */
  divergenceReport: DivergenceReportOutputSchema,

  /** Prescriptive output from structural-suggestions-v1. */
  structuralSuggestions: StructuralSuggestionsOutputSchema,

  /** Composite metadata: timing info from each step. */
  steps: z.object({
    divergenceReport: z.object({ durationMs: z.number() }),
    structuralSuggestions: z.object({ durationMs: z.number() }),
  }).optional(),
});

export type Output = z.infer<typeof OutputSchema>;
